import React, { useState } from 'react';
import {
  X,
  Check,
  RotateCcw,
  Plus,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Clock,
  Loader2,
  MessageSquare,
  User,
  FileText,
  Quote,
} from 'lucide-react';
import { Button } from './ui/button';
import type { EditorIssue, EditorFeedbackCard, LanguageCode } from '../types';

// RTL languages
const RTL_LANGUAGES: LanguageCode[] = ['fa-AF', 'ps-AF'];

interface EditorReviewPanelProps {
  storyId: string;
  language: LanguageCode;
  isTvStory: boolean;
  authorName?: string;
  submittedAt?: string;
  version: number;
  saving: boolean;
  // AI issues (with status tracking)
  aiIssues: EditorIssue[];
  // Editor feedback cards
  editorFeedback: EditorFeedbackCard[];
  onAddFeedback: (card: Omit<EditorFeedbackCard, 'id' | 'createdAt' | 'createdBy' | 'createdByName'>) => void;
  onRemoveFeedback: (id: string) => void;
  // Actions
  onApprove: () => void;
  onReject: (notes: string) => void;
  onClose: () => void;
  // Text selection capture from editor fields
  getTextSelection?: () => { text: string; location: EditorFeedbackCard['location'] } | null;
}

const SEVERITY_CONFIG: Record<string, { label: string; color: string; dot: string }> = {
  must_fix: { label: 'Must Fix', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400', dot: 'bg-red-500' },
  suggestion: { label: 'Suggestion', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', dot: 'bg-blue-500' },
  note: { label: 'Note', color: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400', dot: 'bg-zinc-400' },
};

const LOCATION_OPTIONS_TV = [
  { value: 'anchor_intro', label: 'Anchor Intro' },
  { value: 'script', label: 'Script' },
  { value: 'headline', label: 'Headline' },
  { value: 'deck', label: 'Deck' },
  { value: 'body', label: 'Body' },
];

const LOCATION_OPTIONS_WEB = [
  { value: 'headline', label: 'Headline' },
  { value: 'deck', label: 'Deck' },
  { value: 'body', label: 'Body' },
];

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// Issue type badge color
function issueTypeBadgeClass(type?: string): string {
  switch (type) {
    case 'grammar': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
    case 'demonym': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
    case 'format': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400';
    case 'terminology': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
    case 'style': return 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400';
    case 'attribution': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400';
    case 'sourcing': return 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400';
    case 'logic': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';
    case 'completeness': return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400';
    case 'balance': return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400';
    case 'fact_check': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400';
    default: return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400';
  }
}

function IssueBadges({ issue }: { issue: EditorIssue }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {issue.type && (
        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${issueTypeBadgeClass(issue.type)}`}>
          {issue.type === 'fact_check' ? 'fact check' : issue.type}
        </span>
      )}
      {issue.priority && (
        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
          issue.priority === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
          issue.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
          issue.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
          'bg-gray-100 dark:bg-gray-900/30 text-gray-500 dark:text-gray-400'
        }`}>
          {issue.priority}
        </span>
      )}
      {issue.location && (
        <span className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase">
          {issue.location.replace('_', ' ')}
        </span>
      )}
      {issue.source && (
        <span className="text-[10px] text-zinc-400 dark:text-zinc-500">
          {issue.source.replace('_', ' ')}
        </span>
      )}
    </div>
  );
}

function IssueCard({ issue, variant }: { issue: EditorIssue; variant: 'dismissed' | 'applied' | 'pending' }) {
  return (
    <div className={`py-2 pl-3 border-l-2 ${
      variant === 'dismissed' ? 'border-red-400 dark:border-red-600' :
      variant === 'applied' ? 'border-green-400 dark:border-green-600' :
      'border-zinc-300 dark:border-zinc-600'
    }`}>
      <IssueBadges issue={issue} />
      <p className="text-[13px] text-zinc-700 dark:text-zinc-300 mt-0.5 leading-relaxed">{issue.text}</p>
      {issue.suggestion && (
        <p className="text-xs text-green-600 dark:text-green-500 mt-1">
          &rarr; &quot;{issue.suggestion}&quot;
        </p>
      )}
      {variant === 'dismissed' && issue.dismissedAt && (
        <p className="text-[10px] text-red-500/70 dark:text-red-400/60 mt-0.5">
          Dismissed {formatRelativeTime(issue.dismissedAt)}
        </p>
      )}
      {variant === 'applied' && issue.appliedAt && (
        <p className="text-[10px] text-green-500/70 dark:text-green-400/60 mt-0.5">
          Applied {formatRelativeTime(issue.appliedAt)}
        </p>
      )}
    </div>
  );
}

/** Section label — lightweight divider using typography */
function SectionLabel({ icon, label, count, color = 'text-zinc-500 dark:text-zinc-400', children }: {
  icon?: React.ReactNode;
  label: string;
  count?: number;
  color?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mt-5 mb-2">
      <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${color}`}>
        {icon}
        {label}
        {count !== undefined && <span className="opacity-60">({count})</span>}
      </div>
      {children}
    </div>
  );
}

export default function EditorReviewPanel({
  language,
  isTvStory,
  authorName,
  submittedAt,
  version,
  saving,
  aiIssues,
  editorFeedback,
  onAddFeedback,
  onRemoveFeedback,
  onApprove,
  onReject,
  onClose,
  getTextSelection,
}: EditorReviewPanelProps) {
  const isRTL = RTL_LANGUAGES.includes(language);
  const textDirection = isRTL ? 'rtl' : 'ltr';
  const locationOptions = isTvStory ? LOCATION_OPTIONS_TV : LOCATION_OPTIONS_WEB;

  // Inline feedback form state
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [fbLocation, setFbLocation] = useState<EditorFeedbackCard['location']>(isTvStory ? 'script' : 'body');
  const [fbText, setFbText] = useState('');
  const [fbSuggestion, setFbSuggestion] = useState('');
  const [fbSeverity, setFbSeverity] = useState<'must_fix' | 'suggestion' | 'note'>('suggestion');
  const [fbQuotedText, setFbQuotedText] = useState('');

  // Reject flow
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');

  // Collapsed sections
  const [showApplied, setShowApplied] = useState(false);

  // Categorize AI issues
  const dismissedIssues = aiIssues.filter(i => i.status === 'dismissed');
  const appliedIssues = aiIssues.filter(i => i.status === 'applied');
  const pendingIssues = aiIssues.filter(i => !i.status || i.status === 'pending');
  const pendingFixable = pendingIssues.filter(i => i.category === 'ai_fixable');
  const pendingHuman = pendingIssues.filter(i => i.category === 'human_required');

  const handleOpenFeedbackForm = () => {
    const sel = getTextSelection?.();
    if (sel) {
      setFbQuotedText(sel.text);
      setFbLocation(sel.location);
    } else {
      setFbQuotedText('');
    }
    setShowFeedbackForm(true);
  };

  const handleAddFeedback = () => {
    if (!fbText.trim()) return;
    onAddFeedback({
      location: fbLocation,
      text: fbText.trim(),
      suggestion: fbSuggestion.trim() || undefined,
      quotedText: fbQuotedText.trim() || undefined,
      severity: fbSeverity,
    });
    setFbText('');
    setFbSuggestion('');
    setFbQuotedText('');
    setFbSeverity('suggestion');
    setShowFeedbackForm(false);
  };

  const handleReject = () => {
    let notes = rejectNotes.trim();
    if (!notes && editorFeedback.length > 0) {
      notes = editorFeedback.map((card, i) => {
        const severity = card.severity ? `[${card.severity.replace('_', ' ')}]` : '';
        const loc = card.location ? `(${card.location})` : '';
        return `${i + 1}. ${severity} ${loc} ${card.text}`;
      }).join('\n');
    }
    if (!notes) return;
    onReject(notes);
    setRejectNotes('');
    setShowRejectForm(false);
  };

  const canReject = rejectNotes.trim() || editorFeedback.length > 0;

  return (
    <div className="w-96 flex-shrink-0 flex flex-col bg-white dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800 h-full animate-slide-in-from-right">
      {/* ── Header + actions (sticky) ── */}
      <div className="flex-shrink-0 px-4 pt-3 pb-2 border-b border-zinc-200 dark:border-zinc-800">
        {/* Title row */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-zinc-800 dark:text-zinc-200 text-sm flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-500" />
            Editor Review
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 p-0.5">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Meta line */}
        <div className="flex items-center gap-2.5 text-[11px] text-zinc-400 dark:text-zinc-500 mb-3">
          {submittedAt && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatRelativeTime(submittedAt)}
            </span>
          )}
          {authorName && (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {authorName}
            </span>
          )}
          <span className="flex items-center gap-1">
            <FileText className="h-3 w-3" />
            v{version}
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button
            onClick={onApprove}
            disabled={saving}
            className="flex-1 bg-green-600 hover:bg-green-700 text-sm h-8"
            size="sm"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
            Approve
          </Button>
          <Button
            onClick={() => setShowRejectForm(!showRejectForm)}
            variant={showRejectForm ? 'default' : 'outline'}
            className={`flex-1 text-sm h-8 ${showRejectForm ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-200 dark:border-red-800/40 hover:bg-red-50 dark:hover:bg-red-900/20'}`}
            size="sm"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            Send Back
          </Button>
        </div>

        {/* Inline reject form */}
        {showRejectForm && (
          <div className="mt-2 space-y-2">
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              rows={2}
              dir={textDirection}
              placeholder="Notes for journalist (optional if feedback cards exist)..."
              className="w-full text-sm text-zinc-700 dark:text-zinc-300 border border-red-200 dark:border-red-800/40 rounded-lg p-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none bg-white dark:bg-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-600"
            />
            <div className="flex gap-2">
              <Button onClick={() => { setShowRejectForm(false); setRejectNotes(''); }} variant="ghost" size="sm" className="flex-1 text-xs h-7">
                Cancel
              </Button>
              <Button onClick={handleReject} disabled={!canReject} size="sm" className="flex-1 bg-red-600 hover:bg-red-700 text-xs h-7">
                Confirm Send Back
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">

        {/* ── Editor Feedback ── */}
        <SectionLabel
          icon={<MessageSquare className="h-3 w-3" />}
          label="Your Feedback"
          count={editorFeedback.length}
        >
          <button
            onClick={() => showFeedbackForm ? setShowFeedbackForm(false) : handleOpenFeedbackForm()}
            className="flex items-center gap-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          >
            <Plus className="h-3 w-3" />
            Add
          </button>
        </SectionLabel>

        {/* Add feedback form */}
        {showFeedbackForm && (
          <div className="mb-3 p-3 rounded-lg bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200/60 dark:border-blue-800/30 space-y-2">
            <select
              value={fbLocation}
              onChange={(e) => setFbLocation(e.target.value as EditorFeedbackCard['location'])}
              className="w-full text-xs border border-zinc-200 dark:border-zinc-700 rounded px-2 py-1.5 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
            >
              {locationOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            {fbQuotedText && (
              <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/15 rounded px-2.5 py-1.5 border border-amber-200/50 dark:border-amber-800/30">
                <Quote className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800 dark:text-amber-300 italic line-clamp-3 flex-1" dir={textDirection}>
                  {fbQuotedText}
                </p>
                <button onClick={() => setFbQuotedText('')} className="text-amber-400 hover:text-amber-600 flex-shrink-0">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            <textarea
              value={fbText}
              onChange={(e) => setFbText(e.target.value)}
              rows={2}
              dir={textDirection}
              placeholder="What needs attention..."
              className="w-full text-sm text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 rounded-lg p-2 resize-none bg-white dark:bg-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-600"
              autoFocus
            />

            <textarea
              value={fbSuggestion}
              onChange={(e) => setFbSuggestion(e.target.value)}
              rows={1}
              dir={textDirection}
              placeholder="Suggested fix (optional)..."
              className="w-full text-xs text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 rounded-lg p-2 resize-none bg-white dark:bg-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-600"
            />

            <div className="flex items-center gap-1.5">
              {(['must_fix', 'suggestion', 'note'] as const).map(sev => (
                <button
                  key={sev}
                  onClick={() => setFbSeverity(sev)}
                  className={`text-[10px] font-semibold px-2 py-1 rounded transition-all ${
                    fbSeverity === sev
                      ? SEVERITY_CONFIG[sev].color + ' ring-1 ring-current'
                      : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-500 hover:text-zinc-600'
                  }`}
                >
                  {SEVERITY_CONFIG[sev].label}
                </button>
              ))}
            </div>

            <div className="flex gap-2 pt-0.5">
              <Button onClick={() => setShowFeedbackForm(false)} variant="ghost" size="sm" className="flex-1 text-xs h-7">Cancel</Button>
              <Button onClick={handleAddFeedback} disabled={!fbText.trim()} size="sm" className="flex-1 text-xs h-7">Add Feedback</Button>
            </div>
          </div>
        )}

        {/* Feedback cards */}
        {editorFeedback.length > 0 ? (
          <div className="space-y-1.5">
            {editorFeedback.map(card => (
              <div key={card.id} className="py-2 pl-3 border-l-2 border-blue-400 dark:border-blue-600 group">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] uppercase text-zinc-400 dark:text-zinc-500">{card.location.replace('_', ' ')}</span>
                    {card.severity && (
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${SEVERITY_CONFIG[card.severity]?.dot || 'bg-zinc-400'}`} />
                    )}
                    {card.severity && (
                      <span className={`text-[10px] font-medium ${
                        card.severity === 'must_fix' ? 'text-red-500 dark:text-red-400' :
                        card.severity === 'suggestion' ? 'text-blue-500 dark:text-blue-400' :
                        'text-zinc-400 dark:text-zinc-500'
                      }`}>
                        {SEVERITY_CONFIG[card.severity]?.label}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => onRemoveFeedback(card.id)}
                    className="text-zinc-300 hover:text-red-500 dark:text-zinc-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                {card.quotedText && (
                  <p className="text-xs text-amber-700/80 dark:text-amber-400/70 italic mt-0.5 line-clamp-2" dir={textDirection}>
                    &ldquo;{card.quotedText}&rdquo;
                  </p>
                )}
                <p className="text-[13px] text-zinc-700 dark:text-zinc-300 mt-0.5 leading-relaxed" dir={textDirection}>{card.text}</p>
                {card.suggestion && (
                  <p className="text-xs text-green-600 dark:text-green-500 mt-0.5" dir={textDirection}>
                    &rarr; {card.suggestion}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : !showFeedbackForm ? (
          <p className="text-xs text-zinc-400 dark:text-zinc-500 py-1">No feedback yet — select text in the editor, then click Add.</p>
        ) : null}

        {/* ── Dismissed by Journalist ── */}
        {dismissedIssues.length > 0 && (
          <>
            <SectionLabel
              icon={<AlertTriangle className="h-3 w-3" />}
              label="Dismissed by Journalist"
              count={dismissedIssues.length}
              color="text-red-500 dark:text-red-400"
            />
            <div className="space-y-1.5">
              {dismissedIssues.map(issue => (
                <IssueCard key={issue.id} issue={issue} variant="dismissed" />
              ))}
            </div>
          </>
        )}

        {/* ── Active AI Issues ── */}
        {pendingIssues.length > 0 && (
          <>
            <SectionLabel
              icon={<Sparkles className="h-3 w-3" />}
              label="Active AI Issues"
              count={pendingIssues.length}
              color="text-purple-500 dark:text-purple-400"
            />
            {pendingFixable.length > 0 && (
              <div className="mb-2">
                <span className="text-[10px] font-medium text-zinc-400 dark:text-zinc-500 uppercase mb-1 block">
                  AI Fixable ({pendingFixable.length})
                </span>
                <div className="space-y-1.5">
                  {pendingFixable.map(issue => (
                    <IssueCard key={issue.id} issue={issue} variant="pending" />
                  ))}
                </div>
              </div>
            )}
            {pendingHuman.length > 0 && (
              <div>
                <span className="text-[10px] font-medium text-zinc-400 dark:text-zinc-500 uppercase mb-1 block">
                  Needs Attention ({pendingHuman.length})
                </span>
                <div className="space-y-1.5">
                  {pendingHuman.map(issue => (
                    <IssueCard key={issue.id} issue={issue} variant="pending" />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Applied Issues (collapsible) ── */}
        {appliedIssues.length > 0 && (
          <>
            <div className="mt-5 mb-2">
              <button
                onClick={() => setShowApplied(!showApplied)}
                className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-green-500 dark:text-green-400 hover:text-green-600 transition-colors"
              >
                {showApplied ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                <Check className="h-3 w-3" />
                {appliedIssues.length} Issue{appliedIssues.length !== 1 ? 's' : ''} Addressed
              </button>
            </div>
            {showApplied && (
              <div className="space-y-1.5">
                {appliedIssues.map(issue => (
                  <IssueCard key={issue.id} issue={issue} variant="applied" />
                ))}
              </div>
            )}
          </>
        )}

        {/* Empty state */}
        {aiIssues.length === 0 && editorFeedback.length === 0 && !showFeedbackForm && (
          <div className="mt-8 text-center">
            <Sparkles className="h-5 w-5 text-zinc-300 dark:text-zinc-600 mx-auto mb-2" />
            <p className="text-xs text-zinc-400 dark:text-zinc-500">No AI issues found for this story.</p>
            <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">Add your own feedback above.</p>
          </div>
        )}

        {/* Footer hint */}
        <p className="text-[10px] text-zinc-300 dark:text-zinc-600 mt-6 italic">
          Direct edits are tracked in Version History.
        </p>
      </div>
    </div>
  );
}
